import React, { useState, useEffect, useCallback } from "react";
import { FiSend, FiLoader } from "react-icons/fi";
import voiceService from "../services/voiceService";
import emailService from "../services/emailService";
import {
  TEMPLATE_TYPES,
  TEMPLATE_TYPES_EMAIL,
  LANGUAGES,
} from "../constants/outboundTestOptions";

const initialForm = {
  templateType: "",
  language: "",
  template: "",
  phone: "",
  email: "",
  address1: "Default address as message template",
  address2: "",
  address3: "",
};

const validatePhoneNumber = (phone) => /^\d{10}$/.test(phone);

const validateEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

/**
 * Shared PSPS outbound test form. Voice and email share template listing;
 * sends use `/api/voice/send-voice` vs `/api/email/send-email`.
 */
const OutboundTestForm = ({
  mode,
  title,
  icon: Icon,
  accentColor,
  submitLabel,
  sendingLabel,
  successMessage,
}) => {
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const templateTypeOptions =
    mode === "email" ? TEMPLATE_TYPES_EMAIL : TEMPLATE_TYPES;

  useEffect(() => {
    setAvailableTemplates([]);
  }, []);

  const fetchTemplates = useCallback(async (templateType, language) => {
    if (!templateType || !language) {
      setAvailableTemplates([]);
      return;
    }

    setLoadingTemplates(true);
    try {
      const response = await voiceService.getTemplates(templateType, language);
      if (response.success && response.data && response.data.templates) {
        const formattedTemplates = response.data.templates.map((template) => ({
          value: template.template_id_bint.toString(),
          label: template.title_vch,
        }));
        setAvailableTemplates(formattedTemplates);
      } else {
        setAvailableTemplates([]);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      setAvailableTemplates([]);
      setSubmitError("Failed to load templates. Please try again.");
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const newForm = { ...prev, [name]: value };

      if (name === "templateType" || name === "language") {
        newForm.template = "";
        newForm.address1 = "Default address as message template";
        if (name === "templateType" && newForm.language) {
          fetchTemplates(value, newForm.language);
        } else if (name === "language" && newForm.templateType) {
          fetchTemplates(newForm.templateType, value);
        }
      }

      if (name === "template") {
        const selectedTemplate = availableTemplates.find(
          (t) => t.value === value
        );
        if (selectedTemplate) {
          newForm.address1 = selectedTemplate.label;
        } else {
          newForm.address1 = "Default address as message template";
        }
      }

      return newForm;
    });

    if (submitMessage || submitError) {
      setSubmitMessage("");
      setSubmitError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.templateType ||
      !form.language ||
      !form.template ||
      (mode === "voice" && !form.phone) ||
      (mode === "email" && !form.email)
    ) {
      setSubmitError("Please fill in all required fields.");
      return;
    }

    if (mode === "voice" && !validatePhoneNumber(form.phone)) {
      setSubmitError("Please enter a valid 10-digit phone number.");
      return;
    }

    if (mode === "email" && !validateEmail(form.email)) {
      setSubmitError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitMessage("");

    try {
      const selectedTemplate = availableTemplates.find(
        (t) => t.value === form.template
      );
      const selectedLanguage = LANGUAGES.find((l) => l.value === form.language);

      if (!selectedTemplate) {
        setSubmitError("Please select a valid template.");
        setIsSubmitting(false);
        return;
      }

      const basePayload = {
        messageTemplate: selectedTemplate.label,
        templateId: parseInt(form.template, 10),
        language: selectedLanguage.value,
        address1: form.address1,
        address2: form.address2,
        address3: form.address3,
      };

      const payload =
        mode === "voice"
          ? { ...basePayload, phone: form.phone }
          : { ...basePayload, email: form.email.trim() };

      const response =
        mode === "voice"
          ? await voiceService.sendVoiceCall(payload)
          : await emailService.sendTestEmail(payload);

      if (response.success) {
        setSubmitMessage(successMessage);
        setForm({
          ...initialForm,
          address1: "Default address as message template",
        });
        setAvailableTemplates([]);
      } else {
        setSubmitError(
          response.message ||
            `Failed to send ${mode === "voice" ? "voice call" : "test email"}. Please try again.`
        );
      }
    } catch (error) {
      setSubmitError(
        error.message ||
          `An error occurred while sending the ${mode === "voice" ? "voice call" : "test email"}. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dashboard-card" style={{ marginBottom: "2rem" }}>
      <div className="card-header">
        <h3 className="card-title">{title}</h3>
        {Icon && (
          <Icon className="card-icon" style={{ color: accentColor }} />
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              Template Type <span style={{ color: "#e74c3c" }}>*</span>
            </label>
            <select
              name="templateType"
              value={form.templateType}
              onChange={handleInputChange}
              className="form-input"
              required
            >
              <option value="">Select Template Type</option>
              {templateTypeOptions.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Language <span style={{ color: "#e74c3c" }}>*</span>
            </label>
            <select
              name="language"
              value={form.language}
              onChange={handleInputChange}
              className="form-input"
              required
            >
              <option value="">Select Language</option>
              {LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">
              Template <span style={{ color: "#e74c3c" }}>*</span>
            </label>
            <select
              name="template"
              value={form.template}
              onChange={handleInputChange}
              className="form-input"
              required
              disabled={
                loadingTemplates || !form.templateType || !form.language
              }
            >
              <option value="">
                {loadingTemplates
                  ? "Loading templates..."
                  : !form.templateType || !form.language
                  ? "Select Template Type and Language first"
                  : "Select Template"}
              </option>
              {availableTemplates.map((template) => (
                <option key={template.value} value={template.value}>
                  {template.label}
                </option>
              ))}
            </select>
            {loadingTemplates && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginTop: "0.5rem",
                  color: "#7f8c8d",
                  fontSize: "0.9rem",
                }}
              >
                <FiLoader className="animate-spin" />
                Loading templates...
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              {mode === "voice" ? "Phone Number" : "Email"}{" "}
              <span style={{ color: "#e74c3c" }}>*</span>
            </label>
            {mode === "voice" ? (
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter 10-digit phone number"
                maxLength="10"
                required
              />
            ) : (
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="recipient@example.com"
                required
              />
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Address 1 (Template Name)</label>
            <input
              type="text"
              name="address1"
              value={form.address1}
              className="form-input"
              placeholder="Select a template to auto-populate"
              disabled
              style={{
                backgroundColor: "#f8f9fa",
                color: "#495057",
                cursor: "not-allowed",
              }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Address 2</label>
            <select
              name="address2"
              value={form.address2}
              onChange={handleInputChange}
              className="form-input"
            >
              <option value="">Select Address 2 (Optional)</option>
              <option value="13th St, New York, NY 10011, USA">
                13th St, New York, NY 10011, USA
              </option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Address 3</label>
            <select
              name="address3"
              value={form.address3}
              onChange={handleInputChange}
              className="form-input"
            >
              <option value="">Select Address 3 (Optional)</option>
              <option value="75 3rd Ave, New York, NY 10003, USA">
                75 3rd Ave, New York, NY 10003, USA
              </option>
            </select>
          </div>
          <div className="form-group" />
        </div>

        {submitMessage && (
          <div
            style={{
              background: "#27ae60",
              color: "white",
              padding: "1rem",
              borderRadius: "5px",
              marginBottom: "1rem",
              textAlign: "center",
            }}
          >
            {submitMessage}
          </div>
        )}

        {submitError && (
          <div
            style={{
              background: "#e74c3c",
              color: "white",
              padding: "1rem",
              borderRadius: "5px",
              marginBottom: "1rem",
              textAlign: "center",
            }}
          >
            {submitError}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            {isSubmitting ? (
              <>
                <FiLoader className="animate-spin" />
                {sendingLabel}
              </>
            ) : (
              <>
                <FiSend />
                {submitLabel}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OutboundTestForm;
